import React, { useRef } from 'react';
import { Pills } from 'barte-design-system';
import './TaxasCartaoGrid.css';

const BANDEIRAS = ['Mastercard', 'Visa', 'Elo', 'Amex', 'Hiper'];
const DEFAULT_BANDEIRA_PADRAO = 'Mastercard';

const RANGES: { key: string; collapsedLabel: string; parcelas: number[] }[] = [
  { key: '1x',    collapsedLabel: '1x',       parcelas: [1] },
  { key: '2-6',   collapsedLabel: '2 a 6x',   parcelas: [2, 3, 4, 5, 6] },
  { key: '7-12',  collapsedLabel: '7 a 12x',  parcelas: [7, 8, 9, 10, 11, 12] },
  { key: '13-24', collapsedLabel: '13 a 24x', parcelas: [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24] },
];

// Estado de valores por linha. Chaves possíveis:
//   - chave do range ('1x', '2-6', '7-12', '13-24') — vale como "1ª parcela do range" quando expandido
//   - chave da parcela individual ('p-3', 'p-4', ..., 'p-24')
// Cada valor é um array de 5 strings (uma por bandeira).
export type TaxasCartaoState = Record<string, string[]>;

interface TaxasCartaoGridProps {
  value: TaxasCartaoState;
  onChange: (next: TaxasCartaoState) => void;
  expanded: Record<string, boolean>; // por range key
  onToggleExpand: (rangeKey: string) => void;
  /** Quando true, células ficam read-only e não recebem clicks/edit/paste. */
  readOnly?: boolean;
  /** Linhas adicionais renderizadas após os ranges (ex.: Débito).
   *  Não são expansíveis. */
  extraRows?: { key: string; label: string }[];
  /** Renderização custom para o conteúdo das células (apenas quando readOnly).
   *  Útil para mostrar diff estilizado, formatação especial, etc. */
  renderCell?: (rowKey: string, colIdx: number, value: string) => React.ReactNode;
  /** Conjunto de células com erro de validação. Chaves são "rowKey|colIdx" (ex.: "1x|2").
   *  Quando presente, a célula recebe a classe `is-error` para destaque visual. */
  errorCells?: Set<string>;
  /** Bandeira marcada como "Padrão" no header. Default: Mastercard. */
  bandeiraPadrao?: string;
}

function emptyRow(): string[] {
  return ['', '', '', '', ''];
}

export function TaxasCartaoGrid({ value, onChange, expanded, onToggleExpand, readOnly = false, extraRows, renderCell, errorCells, bandeiraPadrao = DEFAULT_BANDEIRA_PADRAO }: TaxasCartaoGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  function setCell(rowKey: string, colIdx: number, v: string) {
    const next = { ...value };
    const row = (next[rowKey] ?? emptyRow()).slice();
    row[colIdx] = v;
    next[rowKey] = row;
    onChange(next);
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>, rowKey: string, colIdx: number) {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const visibleRows = getVisibleRowOrder(expanded);
    const startRowIndex = visibleRows.indexOf(rowKey);
    if (startRowIndex < 0) return;
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
    const next = { ...value };
    lines.forEach((line, lineIdx) => {
      const cells = line.includes('\t') ? line.split('\t') : line.split(',');
      const targetRowKey = visibleRows[startRowIndex + lineIdx];
      if (!targetRowKey) return;
      const row = (next[targetRowKey] ?? emptyRow()).slice();
      cells.forEach((val, c) => {
        const targetC = colIdx + c;
        if (targetC < 5) row[targetC] = val.trim();
      });
      next[targetRowKey] = row;
    });
    onChange(next);
  }

  function renderInputRow(
    rowKey: string,
    label: string,
    opts: { depth: 0 | 1; toggle?: React.ReactNode },
  ) {
    const row = value[rowKey] ?? emptyRow();
    return (
      <React.Fragment key={`row-${rowKey}`}>
        <div className={`tcg-row-label${opts.depth === 1 ? ' is-sub' : ''}`} title={label}>
          <span className="tcg-row-label-text">{label}</span>
          {opts.toggle}
        </div>
        {BANDEIRAS.map((_, c) => {
          const isError = errorCells?.has(`${rowKey}|${c}`) ?? false;
          const errCls = isError ? ' is-error' : '';
          if (readOnly && renderCell) {
            return (
              <div
                key={`${rowKey}-${c}`}
                className={`tcg-cell${opts.depth === 1 ? ' is-sub' : ''} is-readonly tcg-cell--custom${errCls}`}
              >
                {renderCell(rowKey, c, row[c] ?? '')}
              </div>
            );
          }
          return (
            <input
              key={`${rowKey}-${c}`}
              className={`tcg-cell${opts.depth === 1 ? ' is-sub' : ''}${readOnly ? ' is-readonly' : ''}${errCls}`}
              value={row[c] ?? ''}
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : 0}
              onChange={(e) => !readOnly && setCell(rowKey, c, e.target.value)}
              onPaste={(e) => !readOnly && handlePaste(e, rowKey, c)}
            />
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <div className="tcg-wrapper">
      <div className="tcg-grid" ref={gridRef}>
        {/* Header */}
        <div className="tcg-corner">Parcela</div>
        {BANDEIRAS.map((b) => (
          <div key={b} className="tcg-col-header">
            <span className="tcg-col-header-label">{b}</span>
            {b === bandeiraPadrao && (
              <Pills label="Padrão" state="info" variant="fill" size="sm" />
            )}
          </div>
        ))}

        {/* Linhas extras (ex.: Débito) — renderizadas ANTES dos ranges */}
        {extraRows?.map((er) => (
          <React.Fragment key={`extra-top-${er.key}`}>
            {renderInputRow(er.key, er.label, { depth: 0 })}
          </React.Fragment>
        ))}

        {RANGES.map((range) => {
          const expandable = range.parcelas.length > 1;
          const isExpanded = expanded[range.key] === true;
          // Label do range muda quando expandido: vira a primeira parcela individual.
          const firstParcela = range.parcelas[0];
          const labelRange = isExpanded ? `${firstParcela}x` : range.collapsedLabel;
          const toggle = expandable ? (
            <button
              type="button"
              className={`tcg-toggle${isExpanded ? ' is-expanded' : ''}`}
              onClick={() => onToggleExpand(range.key)}
              aria-label={isExpanded ? `Recolher ${range.collapsedLabel}` : `Expandir ${range.collapsedLabel}`}
              aria-expanded={isExpanded}
            >
              {isExpanded ? '−' : '+'}
            </button>
          ) : null;
          return (
            <React.Fragment key={`range-${range.key}`}>
              {renderInputRow(range.key, labelRange, { depth: 0, toggle })}
              {isExpanded && range.parcelas.slice(1).map((p) =>
                renderInputRow(`p-${p}`, `${p}x`, { depth: 1 }),
              )}
            </React.Fragment>
          );
        })}

      </div>
    </div>
  );
}

function getVisibleRowOrder(expanded: Record<string, boolean>): string[] {
  const out: string[] = [];
  for (const range of RANGES) {
    out.push(range.key);
    if (expanded[range.key]) {
      for (const p of range.parcelas.slice(1)) out.push(`p-${p}`);
    }
  }
  return out;
}
